import React, { createContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import apiService from '../services/api';

// Create the authentication context
export const AuthContext = createContext();

// Returns the storage that currently holds the auth token, or localStorage as default.
// sessionStorage takes priority (used when "remember me" is unchecked).
export const getAuthStorage = () => {
  if (sessionStorage.getItem('disneyShopperToken')) return sessionStorage;
  return localStorage;
};

const clearAuthStorage = () => {
  localStorage.removeItem('disneyShopperToken');
  localStorage.removeItem('disneyShopperUser');
  sessionStorage.removeItem('disneyShopperToken');
  sessionStorage.removeItem('disneyShopperUser');
};

// Create the authentication provider component
export const AuthProvider = ({ children }) => {
  // Try to load user from whichever storage holds the session
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const storedUser = getAuthStorage().getItem('disneyShopperUser');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Error loading user from storage:', error);
      return null;
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState(!!currentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Save user to whichever storage holds the token
  useEffect(() => {
    if (currentUser) {
      getAuthStorage().setItem('disneyShopperUser', JSON.stringify(currentUser));
    } else {
      clearAuthStorage();
    }
    setIsAuthenticated(!!currentUser);
  }, [currentUser]);

  // Load user profile on startup if JWT exists (always fetch fresh to get latest flags like isFriendsAndFamily)
  useEffect(() => {
    const token = getAuthStorage().getItem('disneyShopperToken');
    if (token) {
      getProfile();
    }
  }, []);

  // Get current user profile
  const getProfile = async () => {
    try {
      const response = await apiService.auth.getProfile();
      if (response.data.success) {
        const user = response.data.data;
        setCurrentUser(user);
        Sentry.setUser({ id: user._id, email: user.email });
      }
    } catch (err) {
      console.error('Failed to load user profile', err);
      clearAuthStorage();
    }
  };

  // Refresh profile data (for use after profile updates or email verification)
  const refreshProfile = async () => {
    const token = getAuthStorage().getItem('disneyShopperToken');
    if (token) {
      await getProfile();
    }
  };

  // Login function
  const login = async (email, password, rememberMe = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.auth.login({ email, password });

      if (response.data.success) {
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('disneyShopperToken', response.data.token);
        setCurrentUser(response.data.user);
        return true;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);
    try {
      await apiService.auth.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuthStorage();
      setCurrentUser(null);
      Sentry.setUser(null);
      setIsLoading(false);
    }
  };

  // Register function (always remembers — user just signed up)
  const register = async (userData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.auth.register(userData);

      if (response.data.success) {
        localStorage.setItem('disneyShopperToken', response.data.token);
        setCurrentUser(response.data.user);
        return true;
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create value object with all authentication functionality
  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    refreshProfile
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for easier auth access
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};